import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { FundCoordinatorDtoOut } from '@/models/out/FundCoordinatorDtoOut';
import { FundRepresentativeDtoOut } from '@/models/out/FundRepresentativeDtoOut';
import { FundManagementDetailsDtoIn } from '@/models/in/FundManagementDetailsDtoIn';
import editIcon from '@/assets/icons/edit.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import addIcon from '@/assets/icons/add.svg';
import { toLocalPhone } from '@/helpers/phoneHelper';
import { buildUpdateDtoFromFund, emptyCoordinator, validateCoordinators } from './fundViewForm';
import useUpdateFund from './useUpdateFund';
import styles from './FundDialogs.module.css';
import { toHebrewName } from '@/helpers/hebrewName';

interface EditCoordinatorsDialogProps {
  open: boolean;
  onClose: () => void;
  fund: FundManagementDetailsDtoIn;
}

const EditCoordinatorsDialog = ({ open, onClose, fund }: EditCoordinatorsDialogProps) => {
  const { t } = useTranslation();
  const [coordinators, setCoordinators] = useState<FundCoordinatorDtoOut[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { save, isSaving, serverError, clearServerError } = useUpdateFund(String(fund.id), onClose);

  /*
   * Removing someone here also removes them from the fund's representatives, founder included
   * (the server does it - a founder who can't sign in is nobody's founder). Taking away every
   * founder leaves nobody who adds coordinators or sets permissions, so that one save asks first.
   * Without the list (it failed to load) the save just goes ahead, as it always did.
   */
  const { data: representatives } = useGetAuthApi<FundRepresentativeDtoOut[]>(open ? `funds/financial-data/${fund.id}/representatives` : '');
  const [foundersLeaving, setFoundersLeaving] = useState<FundRepresentativeDtoOut[] | null>(null);

  useEffect(() => {
    if (open) {
      // Phones are stored in E.164; edit them in the local form people typed. The server
      // re-normalizes on save, so what goes back out is unchanged in shape.
      setCoordinators((fund.coordinators ?? []).map((coordinator) => ({
        ...coordinator,
        phone: toLocalPhone(coordinator.phone),
      })));
      setEditingIndex(null);
      setNote('');
      setValidationError(null);
      setFoundersLeaving(null);
      clearServerError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fund]);

  const updateCoordinator = (index: number, changes: Partial<FundCoordinatorDtoOut>) => {
    setCoordinators(coordinators.map((coordinator, i) => (i === index ? { ...coordinator, ...changes } : coordinator)));
  };

  const removeCoordinator = (index: number) => {
    setCoordinators(coordinators.filter((_, i) => i !== index));
    setEditingIndex(null);
  };

  const addCoordinator = () => {
    setCoordinators([...coordinators, emptyCoordinator()]);
    setEditingIndex(coordinators.length);
  };

  const handleSave = () => {
    const error = validateCoordinators(coordinators);
    if (error) {
      setValidationError(error);
      return;
    }
    if (!note.trim()) {
      setValidationError('fundsManagement.noteDialog.noteRequired');
      return;
    }
    setValidationError(null);

    // An email changed here counts as leaving: the server matches people by email.
    const founders = (representatives ?? []).filter((rep) => rep.isFounder);
    const staying = new Set(coordinators.map((coordinator) => coordinator.email.trim().toLowerCase()));
    const leaving = founders.filter((founder) => !staying.has((founder.email ?? '').trim().toLowerCase()));
    if (founders.length > 0 && leaving.length === founders.length) {
      setFoundersLeaving(leaving);
      return;
    }

    saveCoordinators();
  };

  const saveCoordinators = () => {
    setFoundersLeaving(null);
    save(buildUpdateDtoFromFund(fund, {
      coordinators: coordinators.map((coordinator) => ({
        firstName: coordinator.firstName.trim(),
        lastName: coordinator.lastName.trim(),
        email: coordinator.email.trim(),
        phone: coordinator.phone?.trim() ?? '',
      })),
    }, note.trim()));
  };

  const displayName = (coordinator: FundCoordinatorDtoOut) =>
    `${coordinator.firstName} ${coordinator.lastName}`.trim() || t('fundsManagement.newCoordinator');

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={t('fundsManagement.editCoordinatorsTitle')}
      contentClassName={styles.wideDialogContent}
    >
      <div className={styles.dialogForm}>
        <div className={styles.peopleList}>
          {coordinators.map((coordinator, index) => (
            editingIndex === index ? (
              <div key={index} className={styles.dialogListItem}>
                <div className={styles.dialogListItemHeader}>
                  <span className={styles.personName}>{displayName(coordinator)}</span>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    disabled={coordinators.length === 1}
                    title={coordinators.length === 1 ? t('fundsManagement.cannotRemoveLastCoordinator') : undefined}
                    aria-label={t('fundsManagement.remove')}
                    onClick={() => removeCoordinator(index)}
                  >
                    <img src={deleteIcon} alt="" />
                  </button>
                </div>
                <div className={styles.fieldsGrid}>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.firstName')}</label>
                    <Input value={coordinator.firstName} onChange={(e) => updateCoordinator(index, { firstName: toHebrewName(e.target.value) })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.lastName')}</label>
                    <Input value={coordinator.lastName} onChange={(e) => updateCoordinator(index, { lastName: toHebrewName(e.target.value) })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.phone')}</label>
                    <Input dir="ltr" value={coordinator.phone ?? ''} onChange={(e) => updateCoordinator(index, { phone: e.target.value })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.email')}</label>
                    <Input dir="ltr" value={coordinator.email} onChange={(e) => updateCoordinator(index, { email: e.target.value })} />
                  </div>
                </div>
                <Button variant="white" className="self-start" onClick={() => setEditingIndex(null)}>
                  {t('fundsManagement.done')}
                </Button>
              </div>
            ) : (
              <div key={index} className={styles.personListRow}>
                <div className={styles.personSummary}>
                  <span className={styles.personName}>{displayName(coordinator)}</span>
                  <span className={styles.personMetaLine} dir="ltr">
                    {[coordinator.phone, coordinator.email].filter(Boolean).join(' • ')}
                  </span>
                </div>
                <div className={styles.rowActions}>
                  <button type="button" className={styles.rowEditBtn} onClick={() => setEditingIndex(index)}>
                    <img src={editIcon} alt="" />
                    {t('fundsManagement.editButton')}
                  </button>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    disabled={coordinators.length === 1}
                    title={coordinators.length === 1 ? t('fundsManagement.cannotRemoveLastCoordinator') : undefined}
                    aria-label={t('fundsManagement.remove')}
                    onClick={() => removeCoordinator(index)}
                  >
                    <img src={deleteIcon} alt="" />
                  </button>
                </div>
              </div>
            )
          ))}
        </div>

        <Button variant="outline" className={`self-start !inline-flex items-center gap-1.5 ${styles.addBtn}`} onClick={addCoordinator}>
          <img src={addIcon} alt="" />
          {t('fundsManagement.addCoordinator')}
        </Button>

        <div className={styles.formField}>
          <label className={styles.fieldLabel}>{t('fundsManagement.noteDialog.noteLabel')}</label>
          <textarea
            className={styles.dialogTextarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('fundsManagement.noteDialog.notePlaceholder')}
          />
        </div>

        {validationError && <p className={styles.formError}>{t(validationError)}</p>}
        {serverError && <p className={styles.formError}>{serverError}</p>}

        <div className={styles.formActions}>
          <Button variant="white" onClick={onClose} disabled={isSaving}>
            {t('fundsManagement.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('loading') : t('fundsManagement.save')}
          </Button>
        </div>
      </div>

      <ConfirmationDialog
        open={!!foundersLeaving}
        onOpenChange={(isOpen) => { if (!isOpen) setFoundersLeaving(null); }}
        title={t('fundsManagement.lastFounderLeavingTitle')}
        message={t(foundersLeaving && foundersLeaving.length > 1 ? 'fundsManagement.lastFoundersLeavingMessage' : 'fundsManagement.lastFounderLeavingMessage', {
          names: (foundersLeaving ?? []).map((founder) => `${founder.firstName} ${founder.lastName}`.trim()).join(', '),
        })}
        confirmText="fundsManagement.lastFounderLeavingConfirm"
        variant="danger"
        isLoading={isSaving}
        onConfirm={saveCoordinators}
        onCancel={() => setFoundersLeaving(null)}
      />
    </MainDialogLayout>
  );
};

export default EditCoordinatorsDialog;
